import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCalculator } from '../../context/CalculatorContext';
import { registrarContacto } from '../../services/contactService';
import Swal from 'sweetalert2';
import styles from './Contact.module.css';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import Ave from '../../assets/Ave.svg';

function Contact() {
  const navigate = useNavigate();
  const { email, setEmail, empresa, setEmpresa, hasAcceptedPolicy, setHasAcceptedPolicy } = useCalculator();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Mostrar popup al cargar el componente solo si no ha aceptado la política
  useEffect(() => {
    if (!hasAcceptedPolicy) {
      const timer = setTimeout(() => {
        mostrarPopupPoliticas();
      }, 100);
      
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAcceptedPolicy]);

  const mostrarPopupPoliticas = () => {
    Swal.fire({
      title: '<strong>TÉRMINOS Y CONDICIONES DE USO DE LA HERRAMIENTA</strong>',
      html: `
        <div style="text-align: left; max-height: 500px; overflow-y: auto; padding: 10px;" id="terminos-container">
          <h3 style="color: #13226a; margin-top: 0; text-align: center;">CALCULADORA AMBIENTAL - EFIGAS S.A. E.S.P.</h3>
          
          <!-- TÍTULO I -->
          <h3 style="color: #13226a; margin-top: 20px; font-size: 16px; border-bottom: 2px solid #13226a; padding-bottom: 5px;">TÍTULO I: OBJETO, ALCANCE Y ACEPTACIÓN</h3>
          
          <h4 style="color: #13226a; margin-top: 15px; font-size: 14px;"><strong>ARTÍCULO 1. OBJETO PRINCIPAL Y FINALIDAD.</strong></h4>
          <p style="font-size: 13px; line-height: 1.6; text-align: justify;">
            La "Calculadora Ambiental" (en adelante, "la Herramienta") está diseñada para facilitar el <strong>cálculo estimativo</strong> de las emisiones de CO₂ generadas por el consumo de combustibles. El propósito primordial de la Herramienta es identificar y cuantificar los beneficios ambientales de migrar del combustible actual (gasolina, diésel, GLP, fuel oil o carbón) hacia el Gas Natural, posicionándolo como un combustible más amigable con el medio ambiente.
          </p>
          
          <h4 style="color: #13226a; margin-top: 15px; font-size: 14px;"><strong>ARTÍCULO 2. FUNCIONALIDADES DE LA HERRAMIENTA</strong></h4>
          <p style="font-size: 13px; line-height: 1.6; text-align: justify;">
            La Herramienta realiza un <strong>análisis de huella de carbono comparativo</strong> (combustible actual vs. Gas Natural) y proporciona los siguientes resultados:
          </p>
          <ol style="font-size: 13px; line-height: 1.6; text-align: justify; margin-left: 20px;">
            <li>Estimación de <strong>reducción de emisiones en Ton/kg CO₂eq/mes</strong>.</li>
            <li><strong>Equivalencias ambientales cuantificadas</strong> (ej. vehículos fuera de circulación, árboles plantados).
              <p style="font-size: 12px; font-style: italic; margin: 10px 0; background: #f0f0f0; padding: 10px; border-radius: 5px;">
                Las equivalencias ambientales presentadas en este reporte (ej. vehículos fuera de circulación, número de árboles plantados, etc.) se han calculado utilizando como referencia la metodología de conversión de la Calculadora de Equivalencias de Gases de Efecto Invernadero (GEI) de la Agencia de Protección Ambiental de los Estados Unidos (U.S. EPA). Es importante destacar que estas equivalencias tienen un propósito meramente ilustrativo y comparativo.
              </p>
            </li>
            <li><strong>Porcentaje de mejora ambiental</strong>: Reducción de Emisiones de CO₂ equivalente a una mejora en la eficiencia Ambiental.</li>
            <li><strong>Volumen de Gas Natural requerido</strong> para producir la misma energía. Como resultado de la operación, se genera un <strong>Informe PDF personalizado</strong> con los resultados del cálculo.</li>
          </ol>
          
          <h4 style="color: #13226a; margin-top: 15px; font-size: 14px;"><strong>ARTÍCULO 3. ACEPTACIÓN DE LOS TÉRMINOS</strong></h4>
          <p style="font-size: 13px; line-height: 1.6; text-align: justify;">
            El uso de la Herramienta implica la aceptación plena, previa e informada de estos Términos y Condiciones. La aceptación incluye la autorización expresa para el Tratamiento de Datos Personales, la cual se perfecciona mediante la aceptación del <em>checkbox</em> dispuesto antes de diligenciar la información de contacto.
          </p>

          <!-- TÍTULO II -->
          <h3 style="color: #13226a; margin-top: 25px; font-size: 16px; border-bottom: 2px solid #13226a; padding-bottom: 5px;">TÍTULO II: LIMITACIÓN DE RESPONSABILIDAD Y NATURALEZA DE LOS RESULTADOS<br/>(DISCLAIMER TÉCNICO VINCULANTE)</h3>
          
          <h4 style="color: #13226a; margin-top: 15px; font-size: 14px;"><strong>ARTÍCULO 4. CARÁCTER ESTIMATIVO Y METODOLOGÍA BASE</strong></h4>
          <p style="font-size: 13px; line-height: 1.6; text-align: justify;">
            El usuario acepta y reconoce que los resultados son <strong>exclusivamente informativos, comparativos</strong> y <strong>aproximados</strong>. La metodología de cálculo está basada en <strong>fórmulas y factores de emisión de herramientas de uso público y abierto</strong> (como la Calculadora de FECOC 2016 de la UPME). Los cálculos se fundamentan en:
          </p>
          <ol style="font-size: 13px; line-height: 1.6; text-align: justify; margin-left: 20px;">
            <li><strong>Poderes caloríficos estándar</strong> de combustibles y factores de emisión de CO₂ por tipo de combustible publicados por la UPME.</li>
            <li><strong>Conversiones energéticas</strong> a equivalentes en Gas Natural. Se aclara que los combustibles no son comparables directamente por su composición química y sistemas de medición, y el ejercicio compara la <strong>energía producida en BTUs</strong> por sus unidades básicas. Se toman como fuentes principales los datos publicados por entidades del sector energético.</li>
          </ol>
          
          <h4 style="color: #13226a; margin-top: 15px; font-size: 14px;"><strong>ARTÍCULO 5. LIMITACIONES EXPRESAS DE LOS CÁLCULOS</strong></h4>
          <p style="font-size: 13px; line-height: 1.6; text-align: justify;">
            EFIGAS S.A. E.S.P. no se hace responsable ni garantiza la exactitud absoluta de los resultados. El usuario acepta que el reporte generado <strong>no constituye una certificación tácita</strong>, una <strong>garantía de ahorro</strong>, ni un <strong>aval de beneficios ambientales</strong>. Específicamente, los cálculos:
          </p>
          <ol style="font-size: 13px; line-height: 1.6; text-align: justify; margin-left: 20px;">
            <li>Se realizan con valores nominales sin tener en cuenta costos ocultos.</li>
            <li>No incorporan factores de ahorro ni beneficios ambientales exactos.</li>
            <li>No tuvieron en cuenta las eficiencias de los combustibles y los equipos.</li>
          </ol>
          
          <h4 style="color: #13226a; margin-top: 15px; font-size: 14px;"><strong>ARTÍCULO 6. PROHIBICIÓN DE USO LEGAL O CERTIFICACIÓN</strong></h4>
          <p style="font-size: 13px; line-height: 1.6; text-align: justify;">
            El usuario acepta que estos cálculos son estimaciones y, por lo tanto, no deben ser usados para fines de certificación o cumplimiento legal.
          </p>
          
          <h4 style="color: #13226a; margin-top: 15px; font-size: 14px;"><strong>ARTÍCULO 7. PROPIEDAD INTELECTUAL</strong></h4>
          <p style="font-size: 13px; line-height: 1.6; text-align: justify;">
            EFIGAS S.A. E.S.P. se reserva los Derechos de Propiedad Intelectual sobre la metodología de cálculo utilizada en la Herramienta.
          </p>

          <!-- TÍTULO III -->
          <h3 style="color: #13226a; margin-top: 25px; font-size: 16px; border-bottom: 2px solid #13226a; padding-bottom: 5px;">TÍTULO III: TRATAMIENTO Y AUTORIZACIÓN DE DATOS PERSONALES</h3>
          
          <h4 style="color: #13226a; margin-top: 15px; font-size: 14px;"><strong>ARTÍCULO 8. TRATAMIENTO DE DATOS Y FINALIDAD COMERCIAL</strong></h4>
          <p style="font-size: 13px; line-height: 1.6; text-align: justify;">
            La Herramienta requiere la captura de información de contacto (ej. correo electrónico corporativo del prospecto, nombre de la empresa interesada). El usuario es informado de que los datos personales suministrados serán tratados conforme a la legislación colombiana vigente. El tratamiento de datos tiene una finalidad comercial, que incluye la generación de leads y el registro del contacto para seguimiento comercial.
          </p>
          
          <h4 style="color: #13226a; margin-top: 15px; font-size: 14px;"><strong>ARTÍCULO 9. RESPONSABLE DEL TRATAMIENTO</strong></h4>
          <p style="font-size: 13px; line-height: 1.6; text-align: justify;">
            El responsable del Tratamiento es <strong>EFIGAS S.A. E.S.P.</strong>, identificada con NIT 800.202.395-3. La entidad está domiciliada en Manizales, con dirección física en la Avenida Kevin Ángel No. 70 - 70, y correo electrónico <strong>ventanillaunica@efigas.com.co</strong>.
          </p>
          
          <h4 style="color: #13226a; margin-top: 15px; font-size: 14px;"><strong>ARTÍCULO 10. AUTORIZACIÓN EXPRESA</strong></h4>
          <p style="font-size: 13px; line-height: 1.6; text-align: justify;">
            Con la aceptación del <em>checkbox</em>, el usuario <strong>autoriza de manera expresa, previa e informada</strong> a EFIGAS S.A. E.S.P., para que en calidad de responsable, pueda directamente o a través de un tercero, tratar sus Datos Personales.
          </p>
          
          <h4 style="color: #13226a; margin-top: 15px; font-size: 14px;"><strong>ARTÍCULO 11. PROCESAMIENTO TEMPORAL DE DATOS</strong></h4>
          <p style="font-size: 13px; line-height: 1.6; text-align: justify;">
            Se informa que la Herramienta no requiere inicio de sesión ni almacenamiento persistente de información del usuario en el navegador. Los datos se procesan únicamente durante la sesión de cálculo para validar la información ingresada, realizar los cálculos energéticos, y generar el informe PDF personalizado.
          </p>

          <!-- TÍTULO IV -->
          <h3 style="color: #13226a; margin-top: 25px; font-size: 16px; border-bottom: 2px solid #13226a; padding-bottom: 5px;">TÍTULO IV: DERECHOS Y PROCEDIMIENTO DEL TITULAR</h3>
          
          <h4 style="color: #13226a; margin-top: 15px; font-size: 14px;"><strong>ARTÍCULO 12. DERECHOS DEL TITULAR</strong></h4>
          <p style="font-size: 13px; line-height: 1.6; text-align: justify;">
            Como titular de los datos, usted tiene el derecho a: acceder, actualizar, rectificar y suprimir sus datos; solicitar prueba de la autorización otorgada; presentar quejas ante la Superintendencia de Industria y Comercio (SIC); y revocar la autorización, salvo que exista alguna obligación legal o contractual que lo impida.
          </p>
          
          <h4 style="color: #13226a; margin-top: 15px; font-size: 14px;"><strong>ARTÍCULO 13. CANALES PARA EJERCICIO DE DERECHOS</strong></h4>
          <p style="font-size: 13px; line-height: 1.6; text-align: justify;">
            Las Peticiones, Quejas y Reclamos (PQR) podrán presentarse a través de los siguientes canales:
          </p>
          <ul style="font-size: 13px; line-height: 1.6; text-align: justify; margin-left: 20px;">
            <li><strong>Dirección física:</strong> Avenida Kevin Ángel No. 70 - 70.</li>
            <li><strong>Correo electrónico:</strong> ventanillaunica@efigas.com.co</li>
            <li><strong>Línea telefónica:</strong> (606) 8983222 o (+57) 6-8982323.</li>
            <li><strong>Portal de PQR:</strong> El portal que se encuentra en la página web de la empresa.</li>
          </ul>
          
          <h4 style="color: #13226a; margin-top: 15px; font-size: 14px;"><strong>ARTÍCULO 14. TÉRMINOS DE LEY</strong></h4>
          <p style="font-size: 13px; line-height: 1.6; text-align: justify;">
            Este documento será gobernado e interpretado de acuerdo con las leyes de Colombia, sin dar efecto a cualquier principio de conflictos de ley. Si alguna disposición resulta ilegal, o presenta un vacío, o por cualquier razón resulta inaplicable, la misma deberá ser interpretada dentro del marco de estas disposiciones y en cualquier caso no afectará la validez y la aplicabilidad de las provisiones restantes.
          </p>

          <!-- AUTORIZACIÓN -->
          <div id="autorizacion-section" style="background: #f0f0f0; padding: 15px; border-radius: 5px; margin-top: 20px; border: 2px solid #13226a;">
            <h4 style="color: #13226a; margin-top: 0; text-align: center;"><strong>AUTORIZACIÓN REQUERIDA</strong></h4>
            <label style="display: flex; align-items: flex-start; cursor: pointer; font-size: 12px; line-height: 1.5;">
              <input type="checkbox" id="swal-checkbox-accept" style="margin-right: 10px; margin-top: 3px; min-width: 18px; height: 18px; flex-shrink: 0;">
              <span style="text-align: justify;">
                Con la aceptación del checkbox autoriza de manera expresa, previa e informada, a <strong>EFIGAS S.A. E.S.P.</strong>, empresa identificada con <strong>NIT 800.202.395-3</strong>, domiciliada en Manizales, con dirección física en la <strong>Avenida Kevin Ángel No. 70 - 70</strong>, correo electrónico <strong>ventanillaunica@efigas.com.co</strong> y teléfono <strong>(+57) 6-8982323</strong>, para que en calidad de Responsable, pueda directamente o a través de un tercero, tratar sus Datos Personales, para las finalidades específicas que corresponden a su grupo de interés, contenidas en la Política de Privacidad y Protección de Datos Personales disponible en <a href="https://www.efigas.com.co" target="_blank" style="color: #13226a; text-decoration: underline;">https://www.efigas.com.co</a>, y en particular, para las siguientes: <strong>(i)</strong> Realizar transmisión y/o transferencia de los datos. <strong>(ii)</strong> Atención de PQRs. <strong>(iii)</strong> Contacto a través de correo electrónico para remisión del reporte de resultados de la Calculadora Ambiental de la Transición. <strong>(iv)</strong> Contacto a través de canales autorizados para presentarle información relevante sobre proyectos de sostenibilidad, eficiencia energética, calidad del aire y cambio climático en el Eje Cafetero, remisión de boletines, comunicados, y demás información sobre los servicios ofrecidos por la empresa y sus aliados. <strong>(v)</strong> Realizar el seguimiento de los indicadores de uso de la herramienta para fines estadísticos, de mejora del servicio y de gestión ambiental y climática. <strong>(vi)</strong> Almacenar la información para fines estadísticos y/o históricos. <strong>(vii)</strong> Generar reportes internos para toma de decisiones sobre gestión de proyectos ambientales.
                <br/><br/>
                Como titular, usted tiene derecho a: acceder, actualizar, rectificar y suprimir sus datos; solicitar prueba de la autorización; presentar quejas ante la SIC; y revocar la autorización, salvo que exista alguna obligación legal o contractual que lo impida. Las PQR podrá presentarlas a la dirección física indicada, al correo <strong>ventanillaunica@efigas.com.co</strong>, a la línea <strong>(606) 8983222</strong>, o en el portal de PQR que se encuentra en la página web de la empresa.
              </span>
            </label>
            <div style="margin-top: 10px; text-align: center;">
              <a href="/PL-GI-80.pdf" target="_blank" style="color: #13226a; text-decoration: underline; font-size: 13px; font-weight: bold;">
                📄 Leer Política Completa de Tratamiento de Datos Personales
              </a>
            </div>
          </div>
          
          <!-- FOOTER INFORMACIÓN -->
          <p style="font-size: 12px; font-style: italic; margin-top: 15px; color: #666; text-align: center; background: #fff3cd; padding: 10px; border-radius: 5px;">
            <strong>⚠️ IMPORTANTE:</strong> Al hacer clic en "Aceptar y Continuar" usted declara haber leído, comprendido y aceptado expresamente estos Términos y Condiciones en su totalidad.
          </p>
        </div>
      `,
      width: '800px',
      showCancelButton: true,
      confirmButtonText: 'Aceptar y Continuar',
      cancelButtonText: '✕',
      confirmButtonColor: '#13226a',
      cancelButtonColor: '#d33',
      allowOutsideClick: false,
      allowEscapeKey: false,
      customClass: {
        popup: 'swal-popup-custom'
      },
      didOpen: () => {
        // ✅ SCROLL AUTOMÁTICO A LA SECCIÓN DE AUTORIZACIÓN (COMPATIBLE CON MÓVILES)
        const container = document.getElementById('terminos-container');
        const authSection = document.getElementById('autorizacion-section');
        
        if (container && authSection) {
          // Delay más largo para asegurar renderizado completo en móviles
          setTimeout(() => {
            // Calcular la posición del elemento dentro del contenedor
            const containerTop = container.getBoundingClientRect().top;
            const authSectionTop = authSection.getBoundingClientRect().top;
            const scrollOffset = authSectionTop - containerTop;
            
            // Hacer scroll al contenedor (más compatible con móviles)
            container.scrollTop = container.scrollTop + scrollOffset - 20; // -20px de margen superior
            
            // Fallback: si el método anterior no funciona, usar scrollIntoView
            setTimeout(() => {
              if (Math.abs(container.scrollTop + scrollOffset - 20) > 50) {
                authSection.scrollIntoView({ 
                  behavior: 'auto', // Cambiado de 'smooth' a 'auto' para mejor compatibilidad
                  block: 'start'
                });
              }
            }, 150);
          }, 200); // Aumentado de 100 a 200ms
        }

        const confirmButton = Swal.getConfirmButton();
        const checkbox = document.getElementById('swal-checkbox-accept');
        
        confirmButton.disabled = true;
        confirmButton.style.opacity = '0.5';
        confirmButton.style.cursor = 'not-allowed';
        
        checkbox.addEventListener('change', () => {
          if (checkbox.checked) {
            confirmButton.disabled = false;
            confirmButton.style.opacity = '1';
            confirmButton.style.cursor = 'pointer';
          } else {
            confirmButton.disabled = true;
            confirmButton.style.opacity = '0.5';
            confirmButton.style.cursor = 'not-allowed';
          }
        });
      },
      preConfirm: () => {
        const checkbox = document.getElementById('swal-checkbox-accept');
        if (!checkbox.checked) {
          Swal.showValidationMessage('Debe aceptar los Términos y Condiciones para continuar');
          return false;
        }
        return true;
      }
    }).then((result) => {
      if (result.isConfirmed) {
        // Usuario aceptó la política
        setHasAcceptedPolicy(true);
        console.log('✅ Términos y Condiciones aceptados');
      } else if (result.isDismissed) {
        // Usuario cerró con X o canceló
        console.log('❌ Términos y Condiciones rechazados, redirigiendo a Home');
        navigate('/');
      }
    });
  };

  const handleBack = () => {
    navigate('/');
  };

  const handleNext = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email || !empresa) {
      setError('Por favor complete todos los campos');
      return;
    }

    setLoading(true);

    try {
      const resultado = await registrarContacto(email, empresa);
      
      if (resultado.success) {
        console.log('✅ Contacto registrado:', resultado.data);
        navigate('/calculator');
      } else {
        setError(resultado.message);
      }
      
    } catch (err) {
      console.error('❌ Error inesperado:', err);
      setError('Error al procesar la solicitud. Intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.contact}>
      <Header />
      <div className={styles.content}>
        <div className={styles.formContainer}>
          <img src={Ave} alt="Ave" className={styles.aveIcon} />
          <h2 className={styles.title}>Información de contacto</h2>
          
          {error && (
            <div style={{
              backgroundColor: '#fee',
              border: '1px solid #fcc',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '20px',
              color: '#c33'
            }}>
              {error}
            </div>
          )}
          
          <form className={styles.contactForm} onSubmit={handleNext}>
            <div className={styles.formGroup}>
              <label htmlFor="email">Correo electrónico</label>
              <div className={styles.inputWrapper}>
                <svg className={styles.icon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                </svg>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
              <span className={styles.placeholder}>Ingrese su correo electrónico corporativo</span>
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="empresa">Empresa</label>
              <div className={styles.inputWrapper}>
                <svg className={styles.icon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
                </svg>
                <input
                  type="text"
                  id="empresa"
                  value={empresa}
                  onChange={(e) => setEmpresa(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
              <span className={styles.placeholder}>Ingrese el nombre de su empresa</span>
            </div>
          </form>
          <div className={styles.buttons}>
            <button 
              type="button" 
              className={styles.backButton} 
              onClick={handleBack}
              disabled={loading}
            >
              Atrás
            </button>
            <button 
              type="button" 
              className={styles.nextButton} 
              onClick={handleNext}
              disabled={loading}
            >
              {loading ? 'Guardando...' : 'Siguiente'}
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default Contact;